import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { LocationSearch } from './SheetBuilder';

const COLOR_SWATCHES = [
  '#22c55e','#1E40AF','#a855f7','#f472b6','#ef4444',
  '#f59e0b','#06B6D4','#6366f1','#ec4899','#14b8a6',
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // 'choose' | 'create-artist' | 'connect-cal' | 'pick-calendars' | 'share-code' | 'access-code' | 'success'
  const [step, setStep] = useState('choose');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [color, setColor] = useState('#22c55e');
  // True when the signed-in user IS the artist (solo artist, not a manager).
  // When set we pass `userId: user.id` to POST /artists, which sets the
  // `artist.userId` linkage the approvals system uses to route per-artist
  // approval items to the right person.
  const [isSelfArtist, setIsSelfArtist] = useState(false);
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // Joined-artist chips so the user can stack multiple codes during the same
  // onboarding session (e.g. crew on multiple tours, family of two artists).
  const [joinedArtists, setJoinedArtists] = useState([]);
  // Optional artist photo as a data URL — server stores it directly and the
  // /artists/:id/photo endpoint decodes the data: URI for emails. Same
  // pattern as the existing Artists page editor.
  const [photo, setPhoto] = useState(null);
  // Tracks a picked-but-not-yet-decoded file so Create can stay disabled
  // while FileReader is still reading. Otherwise a fast tap right after
  // picking submits the artist with `photo: null`.
  const [photoPending, setPhotoPending] = useState(false);
  const photoInputRef = useRef(null);
  const handlePickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoPending(true);
    const reader = new FileReader();
    reader.onload = () => { setPhoto(reader.result); setPhotoPending(false); };
    reader.onerror = () => setPhotoPending(false);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // The newly-created artist record we hold onto between create-artist and
  // connect-cal so we can show the artist's name/color in the connect prompt
  // and pass its id to /calendar/connect.
  const [createdArtist, setCreatedArtist] = useState(null);

  // Picker step (after a successful Google OAuth round-trip). The server
  // intentionally doesn't auto-select calendars to keep personal calendars
  // out of artist sheets; we pre-check primary client-side so the first run
  // still feels productive.
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState(new Set());
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [savingCalendars, setSavingCalendars] = useState(false);

  // Resume the connect-cal step after the OAuth round trip. The server
  // bounces ?step=connected&artistId=... back here when the user came
  // through the onboarding flow (state suffix ":onboarding").
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const artistId  = searchParams.get('artistId');
    const errParam  = searchParams.get('error');
    if (stepParam === 'connected' && artistId) {
      if (errParam) {
        setError(`Couldn't connect calendar: ${errParam}. You can try again or skip.`);
        setCreatedArtist({ id: artistId, name: 'your artist' });
        setStep('connect-cal');
      } else {
        // Drop straight into the picker — the artist is connected on the
        // server, but no calendars are selected yet (server doesn't default
        // to primary). loadCalendarList pre-checks primary so the user can
        // commit with one tap or pick more before continuing. We also
        // refetch the artist so we have its accessCode + name for the
        // subsequent share-code step (the OAuth round-trip clears React state).
        apiFetch(`/artists/${artistId}`).then(a => {
          if (a) setCreatedArtist(a);
        }).catch(() => setCreatedArtist({ id: artistId, name: 'your artist' }));
        setStep('pick-calendars');
        loadCalendarList(artistId);
      }
      // Clean up the URL so a refresh doesn't re-trigger this branch.
      setSearchParams({}, { replace: true });
    }
  }, []); // intentionally empty — only resume on initial mount

  const loadCalendarList = async (artistId) => {
    setLoadingCalendars(true);
    setError('');
    try {
      const resp = await apiFetch(`/calendar/list/${artistId}`);
      setAvailableCalendars(resp.calendars || []);
      // Pre-check the primary calendar — most common single-account case.
      const primary = (resp.calendars || []).find(c => c.primary);
      if (primary) setSelectedCalendarIds(new Set([primary.id]));
    } catch (e) {
      setError("Couldn't load calendars. The connection may have expired — you can skip and reconnect from Settings.");
    } finally {
      setLoadingCalendars(false);
    }
  };

  const toggleCalendar = (id) => {
    setSelectedCalendarIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSaveSelectionAndFinish = async () => {
    if (!createdArtist) return;
    setSavingCalendars(true);
    setError('');
    try {
      await apiFetch(`/calendar/select/${createdArtist.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ calendarIds: Array.from(selectedCalendarIds) })
      });
      goToShareOrFinish();
    } catch (e) {
      setError(e.message || "Couldn't save your selection. Try again or skip.");
    } finally {
      setSavingCalendars(false);
    }
  };

  const handleSkipPicker = () => {
    goToShareOrFinish();
  };

  // After the calendar steps, surface the artist's auto-generated access
  // code so the admin can hand off team access right when they have the
  // most context. If the create response lacked a code (legacy artist or
  // viewer-join path) skip straight to the success/redirect.
  //
  // Async because the OAuth round-trip clears React state — when we resume
  // from `?step=connected&artistId=...` the artist GET may still be in flight
  // (or never have been issued in this session). Refetch on the spot so we
  // don't silently drop the share-code step on a slow response.
  const goToShareOrFinish = async () => {
    let artist = createdArtist;
    if (!artist?.accessCode && artist?.id) {
      try {
        const fresh = await apiFetch(`/artists/${artist.id}`);
        if (fresh) {
          artist = fresh;
          setCreatedArtist(fresh);
        }
      } catch { /* fall through to no-code branch */ }
    }
    if (artist?.accessCode) {
      setStep('share-code');
    } else {
      setSuccessMsg(`${artist?.name || 'Your artist'} is ready.`);
      setStep('success');
      setTimeout(() => navigate('/admin'), 1800);
    }
  };

  const [copyConfirm, setCopyConfirm] = useState(false);
  const handleCopyCode = async () => {
    if (!createdArtist?.accessCode) return;
    try {
      await navigator.clipboard.writeText(createdArtist.accessCode);
      setCopyConfirm(true);
      setTimeout(() => setCopyConfirm(false), 1600);
    } catch { /* clipboard blocked — silent */ }
  };

  const handleNativeShare = async () => {
    if (!createdArtist?.accessCode) return;
    const text = `Join ${createdArtist.name} on Day Sheets — use access code ${createdArtist.accessCode}`;
    if (navigator.share) {
      try { await navigator.share({ text }); } catch { /* user cancelled */ }
    } else {
      handleCopyCode();
    }
  };

  const handleFinishShare = () => {
    setSuccessMsg(`${createdArtist?.name || 'Your artist'} is ready.`);
    setStep('success');
    setTimeout(() => navigate('/admin'), 1500);
  };

  const handleCreateArtist = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/artists', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          city: city.trim() || null,
          color,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          photo: photo || undefined,
          userId: isSelfArtist && user?.id ? user.id : undefined,
        })
      });
      setCreatedArtist({ ...created, color });
      setStep('connect-cal');
    } catch (e) {
      setError(e.message || 'Failed to create artist');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectCalendar = async () => {
    if (!createdArtist) return;
    setSaving(true);
    setError('');
    try {
      // ?from=onboarding tells the server to bounce back here after consent
      // instead of dropping the user on /admin/artists mid-flow.
      const d = await apiFetch(`/calendar/connect/${createdArtist.id}?from=onboarding`);
      if (d.url) window.location.href = d.url;
    } catch (e) {
      setError(e.message || 'Failed to start calendar connect');
      setSaving(false);
    }
  };

  const handleSkipCalendar = () => {
    // Route through goToShareOrFinish so the user still sees their access
    // code. Previously this jumped straight to success, defeating the whole
    // share-code step for anyone who declined GCal.
    goToShareOrFinish();
  };

  const handleRedeemCode = async () => {
    const normalized = code
      .replace(/[–—]/g, '-')
      .trim()
      .toUpperCase();
    if (!normalized) return;
    setSaving(true);
    setError('');
    try {
      const result = await apiFetch('/auth/redeem-code', {
        method: 'POST',
        body: JSON.stringify({ code: normalized })
      });
      // Append to the chip list so the user can keep adding more codes.
      // Each successful redeem links the artist server-side; we only need
      // to keep the list in sync for display.
      setJoinedArtists(prev => prev.find(a => a.id === result.artist.id)
        ? prev
        : [result.artist, ...prev]);
      setCode('');
    } catch (e) {
      setError(e.message || 'Invalid access code');
    } finally {
      setSaving(false);
    }
  };

  const handleFinishViewer = () => {
    if (!joinedArtists.length) return;
    const first = joinedArtists[0];
    setSuccessMsg(joinedArtists.length === 1
      ? `Connected to ${first.name}!`
      : `Connected to ${joinedArtists.length} artists.`);
    setStep('success');
    setTimeout(() => navigate('/viewer'), 1800);
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:32, position:'relative', overflow:'hidden' }}>
      {/* Animated background */}
      <div style={{ position:'absolute', top:'-20%', left:'50%', transform:'translateX(-50%)', width:600, height:600, borderRadius:'50%', background:'radial-gradient(ellipse, rgba(30,64,175,0.08), transparent 70%)', pointerEvents:'none', animation:'onbPulse 8s ease-in-out infinite' }} />
      <div style={{ position:'absolute', bottom:'-10%', right:'-10%', width:400, height:400, borderRadius:'50%', background:'radial-gradient(ellipse, rgba(34,197,94,0.05), transparent 70%)', pointerEvents:'none', animation:'onbPulse 10s ease-in-out infinite reverse' }} />
      <style>{`@keyframes onbPulse{0%,100%{opacity:0.5;transform:translateX(-50%) scale(1)}50%{opacity:1;transform:translateX(-50%) scale(1.1)}}@keyframes onbFadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}@keyframes onbFadeIn{from{opacity:0}to{opacity:1}}`}</style>

      <div style={{ maxWidth:440, width:'100%', textAlign:'center', position:'relative', zIndex:1 }}>

        {/* ═══ CHOOSE PATH ═══ */}
        {step === 'choose' && (<>
          {/* Logo */}
          <div style={{ animation:'onbFadeUp 0.6s ease-out' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:10, marginBottom:24 }}>
              <img src="/icon.png" alt="Day Sheets" style={{ width:44, height:44, borderRadius:14, boxShadow:'0 4px 20px rgba(30,64,175,0.3)' }} />
              <span style={{ fontSize:26, fontWeight:900, color:'var(--text)', letterSpacing:-1 }}>Day Sheets</span>
            </div>
          </div>

          {/* Hero text */}
          <div style={{ animation:'onbFadeUp 0.6s ease-out 0.1s both' }}>
            <h1 style={{ fontSize:36, fontWeight:900, color:'var(--text)', marginBottom:4, letterSpacing:-1.5, lineHeight:1.1 }}>
              Everything your<br/><span style={{ color:'#1E40AF' }}>team needs.</span>
            </h1>
            <p style={{ fontSize:14, color:'var(--text2)', lineHeight:1.6, marginTop:12, marginBottom:28, animation:'onbFadeIn 0.8s ease-out 0.3s both' }}>
              Join the artists and teams who've replaced<br/>the chaos with clarity.
            </p>
          </div>

          {/* Feature cards */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:28, animation:'onbFadeUp 0.6s ease-out 0.15s both' }}>
            {[
              { icon:'fa-calendar-check', color:'#1E40AF', bg:'rgba(30,64,175,0.12)', title:'Smart Events', desc:'Standalone events with artist ownership. Link and unlink from any day sheet.' },
              { icon:'fa-arrows-rotate', color:'#06B6D4', bg:'rgba(6,182,212,0.12)', title:'Calendar Sync', desc:'Pull events from Google Calendar. Push changes back. Zoom links auto-extracted.' },
              { icon:'fa-envelope', color:'#ec4899', bg:'rgba(236,72,153,0.12)', title:'Email Distribution', desc:'Beautiful HTML emails sent to your team. Scheduled sends. Preview before sending.' },
              { icon:'fa-wand-magic-sparkles', color:'#f59e0b', bg:'rgba(245,158,11,0.12)', title:'AI Extraction', desc:'Drop a PDF call sheet or itinerary. Claude AI extracts every event automatically.' },
            ].map((f, i) => (
              <div key={i} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:'16px 14px', textAlign:'left', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:-20, right:-20, width:80, height:80, borderRadius:'50%', background:f.bg, opacity:0.5, pointerEvents:'none' }} />
                <div style={{ width:36, height:36, borderRadius:10, background:f.bg, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:10 }}>
                  <i className={`fa-solid ${f.icon}`} style={{ fontSize:14, color:f.color }}></i>
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:'var(--text)', marginBottom:4 }}>{f.title}</div>
                <div style={{ fontSize:11, color:'var(--text3)', lineHeight:1.4 }}>{f.desc}</div>
              </div>
            ))}
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:12, animation:'onbFadeUp 0.6s ease-out 0.2s both' }}>
            <button onClick={() => { setStep('create-artist'); setError(''); }}
              style={{ width:'100%', padding:18, borderRadius:14, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor:'pointer', boxShadow:'0 4px 16px rgba(30,64,175,0.3)', display:'flex', alignItems:'center', justifyContent:'center', gap:10, transition:'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 24px rgba(30,64,175,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(30,64,175,0.3)'; }}>
              <i className="fa-solid fa-rocket" style={{ fontSize:14 }}></i> Set Up First Artist
            </button>

            <button onClick={() => { setStep('access-code'); setError(''); }}
              style={{ width:'100%', padding:18, borderRadius:14, background:'var(--card)', border:'1.5px solid var(--border)', color:'var(--text)', fontFamily:'inherit', fontSize:16, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:10, transition:'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(30,64,175,0.4)'; e.currentTarget.style.background='rgba(30,64,175,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--card)'; }}>
              <i className="fa-solid fa-key" style={{ fontSize:14, color:'var(--blue)' }}></i> Enter Access Code
            </button>
          </div>

          <p style={{ fontSize:11, color:'var(--text3)', marginTop:24, lineHeight:1.5, animation:'onbFadeIn 1s ease-out 0.5s both' }}>
            Setting up for your team? Create an artist.<br/>
            Joining someone's team? Enter the code they gave you.
          </p>
        </>)}

        {/* ═══ CREATE ARTIST ═══ */}
        {step === 'create-artist' && (<>
          <button onClick={() => setStep('choose')} style={{ background:'none', border:'none', color:'var(--text3)', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:4, marginBottom:16 }}>
            <i className="fa-solid fa-chevron-left" style={{ fontSize:10 }}></i> Back
          </button>

          <div style={{ width:56, height:56, borderRadius:16, background:'linear-gradient(135deg,#1E40AF,#6366f1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#fff', margin:'0 auto 16px' }}>
            <i className="fa-solid fa-music"></i>
          </div>
          <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:4 }}>Set up your artist profile</h2>
          <p style={{ fontSize:13, color:'var(--text3)', marginBottom:24 }}>This could be you, your band, or an artist you manage.</p>

          {/* Optional artist photo — circular picker centered above the name */}
          <div style={{ display:'flex', justifyContent:'center', marginBottom:16 }}>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              <div onClick={() => photoInputRef.current?.click()}
                style={{ width:84, height:84, borderRadius:'50%', border:`2px ${photo ? 'solid' : 'dashed'} ${photo ? '#1E40AF' : 'var(--border)'}`, background:'var(--card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', overflow:'hidden', transition:'border-color 0.15s' }}>
                {photo
                  ? <img src={photo} alt="artist" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
                      <i className="fa-solid fa-camera" style={{ fontSize:18, color:'var(--text3)' }}></i>
                      <span style={{ fontSize:9, fontWeight:700, color:'var(--text3)' }}>Add photo</span>
                    </div>}
              </div>
              <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePickPhoto} style={{ display:'none' }} />
              {photo && (
                <button onClick={() => setPhoto(null)}
                  style={{ background:'none', border:'none', color:'#ef4444', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', padding:0 }}>
                  Remove photo
                </button>
              )}
            </div>
          </div>

          <div style={{ textAlign:'left' }}>
            <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:0.5, display:'block', marginBottom:6, textTransform:'uppercase' }}>Your name, band name, or artist name</label>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Kevin Jonas, The Jonas Brothers"
              autoFocus
              style={{ width:'100%', padding:'14px 16px', borderRadius:12, background:'var(--card)', border:'1.5px solid var(--border)', color:'var(--text)', fontFamily:'inherit', fontSize:15, outline:'none', boxSizing:'border-box', marginBottom:16 }}
              onFocus={e => e.target.style.borderColor='#1E40AF'}
              onBlur={e => e.target.style.borderColor='var(--border)'}
            />

            <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:0.5, display:'block', marginBottom:6, textTransform:'uppercase' }}>Home City <span style={{ fontWeight:400, opacity:0.6 }}>(optional)</span></label>
            <div style={{ marginBottom:16, position:'relative', zIndex:50 }}>
              <LocationSearch value={city} onChange={setCity} />
            </div>

            <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:0.5, display:'block', marginBottom:8, textTransform:'uppercase' }}>Accent Color</label>
            <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
              {COLOR_SWATCHES.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  style={{ width:32, height:32, borderRadius:'50%', background:c, border: color===c ? '3px solid #fff' : '2px solid transparent', cursor:'pointer', boxShadow: color===c ? `0 0 0 2px ${c}` : 'none', outline:'none' }} />
              ))}
            </div>

            {/* "I am this artist" — sets artist.userId so the approvals system
                routes per-artist items to the signed-in user. Toggling on with
                an empty name autofills from their profile so a solo artist
                doesn't have to retype. */}
            <label style={{ display:'flex', alignItems:'flex-start', gap:12, padding:14, borderRadius:12, background:'var(--card)', border:'1.5px solid var(--border)', cursor:'pointer', marginBottom:16 }}>
              <input
                type="checkbox"
                checked={isSelfArtist}
                onChange={e => {
                  const on = e.target.checked;
                  setIsSelfArtist(on);
                  if (on && !name.trim() && user) {
                    const full = [user.firstName, user.lastName].filter(Boolean).join(' ');
                    if (full) setName(full);
                  }
                }}
                style={{ marginTop:2, width:18, height:18, accentColor:'#1E40AF', cursor:'pointer' }}
              />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>I am this artist</div>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:2, lineHeight:1.4 }}>
                  Routes approvals and per-artist actions to you.
                </div>
              </div>
            </label>
          </div>

          {/* Preview */}
          <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:14, marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff', flexShrink:0 }}>
              {name ? name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() : '?'}
            </div>
            <div style={{ textAlign:'left' }}>
              <div style={{ fontSize:14, fontWeight:800, color:'var(--text)' }}>{name || 'Artist Name'}</div>
              <div style={{ fontSize:11, color:'var(--text3)' }}>{city || 'City'}</div>
            </div>
          </div>

          {error && (
            <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', fontSize:13, marginBottom:12, textAlign:'left' }}>
              {error}
            </div>
          )}

          <button onClick={handleCreateArtist} disabled={saving || photoPending || !name.trim()}
            style={{ width:'100%', padding:16, borderRadius:14, background: name.trim() && !photoPending ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'var(--card)', border: name.trim() && !photoPending ? 'none' : '1px solid var(--border)', color: name.trim() && !photoPending ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor: name.trim() && !photoPending ? 'pointer' : 'default', boxShadow: name.trim() && !photoPending ? '0 4px 16px rgba(34,197,94,0.3)' : 'none', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            {saving
              ? <><i className="fa-solid fa-spinner fa-spin"></i> Creating...</>
              : photoPending
                ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving photo…</>
                : <><i className="fa-solid fa-check"></i> Create Artist</>}
          </button>

          {/* Viewer escape hatch — Google signups become admins by default,
              but the same person might also be crew/family on someone else's
              tour. Let them swap to the access-code path without backing out. */}
          <button
            onClick={() => { setStep('access-code'); setError(''); }}
            style={{ background:'none', border:'none', color:'var(--text3)', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:6, marginTop:14 }}>
            <i className="fa-solid fa-key" style={{ fontSize:11 }}></i>
            Joined as a viewer? Enter your code
            <i className="fa-solid fa-chevron-right" style={{ fontSize:9 }}></i>
          </button>
        </>)}

        {/* ═══ CONNECT GOOGLE CALENDAR ═══ */}
        {step === 'connect-cal' && createdArtist && (<>
          {/* Identity card with the artist's accent so the prompt feels personal */}
          <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:14, marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:'50%', background:createdArtist.color || color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff', flexShrink:0 }}>
              {(createdArtist.name || '?').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <div style={{ textAlign:'left', flex:1 }}>
              <div style={{ fontSize:14, fontWeight:800, color:'var(--text)' }}>{createdArtist.name}</div>
              <div style={{ fontSize:11, color:'#22c55e', fontWeight:600 }}>
                <i className="fa-solid fa-circle-check" style={{ marginRight:4 }}></i>
                Created · ready for events
              </div>
            </div>
          </div>

          <div style={{ width:56, height:56, borderRadius:16, background:'linear-gradient(135deg,#06B6D4,#1E40AF)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#fff', margin:'0 auto 16px' }}>
            <i className="fa-solid fa-calendar-check"></i>
          </div>
          <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:4 }}>Sync {createdArtist.name}'s schedule</h2>
          <p style={{ fontSize:13, color:'var(--text3)', marginBottom:24, lineHeight:1.5 }}>
            Pull events straight from Google Calendar so day sheets fill themselves out.
            You can do this later in Settings if you'd rather.
          </p>

          {error && (
            <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', fontSize:13, marginBottom:12, textAlign:'left' }}>
              {error}
            </div>
          )}

          <button onClick={handleConnectCalendar} disabled={saving}
            style={{ width:'100%', padding:16, borderRadius:14, background:'linear-gradient(135deg,#1E40AF,#06B6D4)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor: saving ? 'default' : 'pointer', boxShadow:'0 4px 16px rgba(30,64,175,0.3)', display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:12 }}>
            {saving
              ? <><i className="fa-solid fa-spinner fa-spin"></i> Connecting…</>
              : <><i className="fa-brands fa-google"></i> Connect Google Calendar</>}
          </button>

          <button onClick={handleSkipCalendar} disabled={saving}
            style={{ width:'100%', padding:14, borderRadius:14, background:'transparent', border:'none', color:'var(--text3)', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor: saving ? 'default' : 'pointer' }}>
            Skip for now
          </button>

          <p style={{ fontSize:11, color:'var(--text3)', marginTop:16, lineHeight:1.5 }}>
            You can manage calendars anytime from Settings → Google Calendar.
          </p>
        </>)}

        {/* ═══ PICK CALENDARS ═══ */}
        {step === 'pick-calendars' && (<>
          <div style={{ width:56, height:56, borderRadius:16, background:'linear-gradient(135deg,#06B6D4,#1E40AF)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#fff', margin:'0 auto 16px' }}>
            <i className="fa-solid fa-list-check"></i>
          </div>
          <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:4 }}>Pick which calendars to sync</h2>
          <p style={{ fontSize:13, color:'var(--text3)', marginBottom:24, lineHeight:1.5 }}>
            Only the calendars you choose will pull events into day sheets.<br/>
            Personal calendars stay personal.
          </p>

          {loadingCalendars && availableCalendars.length === 0 ? (
            <div style={{ padding:'40px 20px', color:'var(--text3)' }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize:24 }}></i>
              <div style={{ marginTop:12, fontSize:13 }}>Loading your calendars…</div>
            </div>
          ) : availableCalendars.length === 0 ? (
            <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', fontSize:13, marginBottom:12, textAlign:'left' }}>
              {error || 'No calendars found.'}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16, textAlign:'left' }}>
              {availableCalendars.map(c => {
                const checked = selectedCalendarIds.has(c.id);
                return (
                  <div
                    key={c.id}
                    onClick={() => toggleCalendar(c.id)}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:12, border:`1.5px solid ${checked ? '#1E40AF' : 'var(--border)'}`, background: checked ? 'rgba(30,64,175,0.06)' : 'var(--card)', cursor:'pointer', transition:'all 0.15s' }}
                  >
                    <div style={{ width:12, height:12, borderRadius:'50%', background:c.color || '#1E40AF', flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:checked ? 700 : 600, color:'var(--text)', display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {c.primary ? 'Primary Calendar' : c.name}
                        </span>
                        {c.primary && (
                          <span style={{ fontSize:9, fontWeight:800, padding:'2px 6px', borderRadius:6, background:'rgba(30,64,175,0.15)', color:'#1E40AF', letterSpacing:0.5 }}>PRIMARY</span>
                        )}
                      </div>
                      {c.description && (
                        <div style={{ fontSize:11, color:'var(--text3)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.description}</div>
                      )}
                    </div>
                    <span style={{ fontSize:18, color:checked ? '#1E40AF' : 'var(--border)' }}>
                      <i className={`fa-solid ${checked ? 'fa-circle-check' : 'fa-circle'}`}></i>
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {error && availableCalendars.length > 0 && (
            <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', fontSize:13, marginBottom:12, textAlign:'left' }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSaveSelectionAndFinish}
            disabled={savingCalendars || availableCalendars.length === 0}
            style={{ width:'100%', padding:16, borderRadius:14, background: availableCalendars.length === 0 ? 'var(--card)' : 'linear-gradient(135deg,#22c55e,#16a34a)', border: availableCalendars.length === 0 ? '1px solid var(--border)' : 'none', color: availableCalendars.length === 0 ? 'var(--text3)' : '#fff', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor: savingCalendars || availableCalendars.length === 0 ? 'default' : 'pointer', boxShadow: availableCalendars.length === 0 ? 'none' : '0 4px 16px rgba(34,197,94,0.3)', display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginBottom:12 }}
          >
            {savingCalendars
              ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving…</>
              : selectedCalendarIds.size === 0
                ? <><i className="fa-solid fa-arrow-right"></i> Continue without syncing</>
                : <><i className="fa-solid fa-check"></i> Sync {selectedCalendarIds.size} calendar{selectedCalendarIds.size === 1 ? '' : 's'}</>}
          </button>

          <button
            onClick={handleSkipPicker}
            disabled={savingCalendars}
            style={{ width:'100%', padding:14, borderRadius:14, background:'transparent', border:'none', color:'var(--text3)', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor: savingCalendars ? 'default' : 'pointer' }}
          >
            Skip — pick later
          </button>
        </>)}

        {/* ═══ SHARE ACCESS CODE ═══ */}
        {step === 'share-code' && createdArtist && (<>
          <div style={{ width:56, height:56, borderRadius:16, background:'linear-gradient(135deg,#1E40AF,#7C3AED)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#fff', margin:'0 auto 16px' }}>
            <i className="fa-solid fa-key"></i>
          </div>
          <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:4 }}>
            Invite your team to {createdArtist.name}
          </h2>
          <p style={{ fontSize:13, color:'var(--text3)', marginBottom:20, lineHeight:1.5 }}>
            Anyone with this code can view today's sheet, travel and approvals.
            You can rotate it later from Settings.
          </p>

          {/* Big code card with the artist's accent */}
          <div style={{
            background: `linear-gradient(135deg, ${createdArtist.color || '#1E40AF'}, ${createdArtist.color || '#1E40AF'}AA)`,
            borderRadius: 22, padding: 24, marginBottom: 16, textAlign: 'left'
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, color:'rgba(255,255,255,0.85)', marginBottom:14 }}>
              <i className="fa-solid fa-key" style={{ fontSize:12, color:'#fbbf24' }}></i>
              <span style={{ fontSize:11, fontWeight:800, letterSpacing:1.5 }}>ARTIST ACCESS CODE</span>
            </div>
            <div style={{ fontSize:'clamp(20px, 7vw, 32px)', fontFamily:'monospace', fontWeight:900, color:'#fff', letterSpacing:2, marginBottom:12, wordBreak:'break-all', lineHeight:1.15 }}>
              {createdArtist.accessCode}
            </div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.85)', lineHeight:1.5 }}>
              Share this with anyone joining {createdArtist.name}'s team.
              They'll see today's sheet, travel and approvals automatically.
            </div>
          </div>

          <div style={{ display:'flex', gap:10, marginBottom:14 }}>
            <button onClick={handleCopyCode}
              style={{ flex:1, padding:'12px 14px', borderRadius:12, background:'var(--card)', border:'1.5px solid var(--border)', color:'var(--text)', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className={`fa-solid ${copyConfirm ? 'fa-check' : 'fa-copy'}`} style={{ fontSize:13 }}></i>
              {copyConfirm ? 'Copied!' : 'Copy'}
            </button>
            <button onClick={handleNativeShare}
              style={{ flex:1, padding:'12px 14px', borderRadius:12, background: createdArtist.color || '#1E40AF', border:'none', color:'#fff', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-solid fa-share-nodes" style={{ fontSize:13 }}></i> Share
            </button>
          </div>

          <button onClick={handleFinishShare}
            style={{ width:'100%', padding:16, borderRadius:14, background:'linear-gradient(135deg,#22c55e,#16a34a)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor:'pointer', boxShadow:'0 4px 16px rgba(34,197,94,0.3)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            <i className="fa-solid fa-arrow-right"></i> Continue
          </button>

          <p style={{ fontSize:11, color:'var(--text3)', marginTop:16, lineHeight:1.5 }}>
            You can copy or rotate this code anytime from Settings.
          </p>
        </>)}

        {/* ═══ ACCESS CODE ═══ */}
        {step === 'access-code' && (<>
          <button onClick={() => setStep('choose')} style={{ background:'none', border:'none', color:'var(--text3)', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:4, marginBottom:16 }}>
            <i className="fa-solid fa-chevron-left" style={{ fontSize:10 }}></i> Back
          </button>

          <div style={{ width:56, height:56, borderRadius:16, background:'linear-gradient(135deg,#1E40AF,#6366f1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#fff', margin:'0 auto 16px' }}>
            <i className="fa-solid fa-key"></i>
          </div>
          <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:4 }}>Enter access code</h2>
          <p style={{ fontSize:13, color:'var(--text3)', marginBottom:20, lineHeight:1.5 }}>
            Enter as many codes as you have — one per artist or tour you've been invited to.
          </p>

          {/* Joined chips — show progress as the user stacks codes */}
          {joinedArtists.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16, textAlign:'left' }}>
              {joinedArtists.map(a => (
                <div key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:12, background:'var(--card)', border:'1px solid var(--border)' }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(30,64,175,0.15)', color:'#1E40AF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800 }}>
                    {(a.name || '?').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>{a.name}</div>
                    <div style={{ fontSize:11, color:'#22c55e', fontWeight:600 }}>Joined</div>
                  </div>
                  <i className="fa-solid fa-circle-check" style={{ fontSize:16, color:'#22c55e' }}></i>
                </div>
              ))}
            </div>
          )}

          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            <input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="Access code"
              maxLength={20}
              autoFocus
              style={{ flex:1, minWidth:0, padding:'14px 18px', borderRadius:12, background:'var(--card)', border:'1.5px solid var(--border)', color:'var(--text)', fontFamily:'inherit', fontSize:16, fontWeight:700, textAlign:'center', letterSpacing:2, outline:'none', boxSizing:'border-box', textTransform:'uppercase' }}
              onFocus={e => e.target.style.borderColor='#1E40AF'}
              onBlur={e => e.target.style.borderColor='var(--border)'}
              onKeyDown={e => e.key === 'Enter' && handleRedeemCode()}
            />
            <button
              onClick={handleRedeemCode}
              disabled={saving || code.trim().length < 4}
              style={{ flexShrink:0, padding:'0 18px', borderRadius:12, background: code.trim().length >= 4 ? '#1E40AF' : 'var(--card)', border: code.trim().length >= 4 ? 'none' : '1px solid var(--border)', color: code.trim().length >= 4 ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:14, fontWeight:800, cursor: code.trim().length >= 4 && !saving ? 'pointer' : 'default', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              {saving ? <i className="fa-solid fa-spinner fa-spin"></i> : <><i className="fa-solid fa-plus" style={{ fontSize:11 }}></i> Add</>}
            </button>
          </div>

          {error && (
            <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', fontSize:13, marginBottom:12, textAlign:'left' }}>
              {error}
            </div>
          )}

          {joinedArtists.length > 0 && (
            <button onClick={handleFinishViewer}
              style={{ width:'100%', padding:16, borderRadius:14, background:'linear-gradient(135deg,#22c55e,#16a34a)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor:'pointer', boxShadow:'0 4px 16px rgba(34,197,94,0.3)', display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginTop:4 }}>
              <i className="fa-solid fa-arrow-right"></i> Continue to Day Sheets
            </button>
          )}
        </>)}

        {/* ═══ SUCCESS ═══ */}
        {step === 'success' && (<>
          <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(34,197,94,0.1)', border:'2px solid rgba(34,197,94,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
            <i className="fa-solid fa-check" style={{ fontSize:32, color:'#22c55e' }}></i>
          </div>
          <h2 style={{ fontSize:22, fontWeight:900, color:'#22c55e', marginBottom:8 }}>You're all set!</h2>
          <p style={{ fontSize:14, color:'var(--text2)', lineHeight:1.5 }}>{successMsg}</p>
        </>)}
      </div>
    </div>
  );
}
